import { motion, Transition } from 'framer-motion';
import { forwardRef, useMemo } from 'react';

import { Box, BoxProps } from '@bonusly/design-system';

const COLORS = [
	'#977BED', // Very Sad (1) - purple
	'#8F7DED',
	'#877FED',
	'#7F81ED',
	'#7783ED',
	'#6F85ED',
	'#5F89ED',
	'#578BED',
	'#4F8DED',
	'#478FED',
	'#3F91ED', // Sad (2) - blue
	'#3F95ED',
	'#3F99ED',
	'#3F9DED',
	'#3FA1ED',
	'#3FA5ED',
	'#3FA9ED',
	'#3FB9ED',
	'#3FBDED',
	'#3FC1ED',
	'#3FC5ED',
	'#3FC9ED',
	'#3FCDED',
	'#3FD1ED',
	'#68E7DC', // Happy (4) - teal
	'#65E4D8',
	'#62E1D4',
	'#5FDED0',
	'#5CDBCC',
	'#59D8C8',
	'#56D5C4',
	'#53D2C0',
	'#50CFBC',
	'#4DCCB8',
	'#3EBDA4',
	'#3BBAA0',
	'#38B79C',
	'#35B498',
	'#32B194',
	'#2FAE90',
	'#4BB475', // Very Happy (5) - green
];

const INSIDE_COLORS = '#002418';

// Function to get mood color based on score (1-5)
export const getMoodColor = (score: number): string => {
	const colorIndex = Math.round((score - 1) * 10);
	const clampedIndex = Math.min(Math.max(colorIndex, 0), COLORS.length - 1);
	return COLORS[clampedIndex];
};

// SVG paths for different expressions
const EXPRESSIONS = {
	eyes: [
		['M 9,21 L 9,21', 'M 22,21 L 22,21'], // Very sad
		['M 9.3,20.5 L 9.3,20.8', 'M 22.3,20.5 L 22.3,20.8'],
		['M 9.6,20 L 9.6,20.6', 'M 22.6,20 L 22.6,20.6'],
		['M 9.9,19.5 L 9.9,20.4', 'M 22.9,19.5 L 22.9,20.4'],
		['M 10.2,19 L 10.2,20.2', 'M 23.2,19 L 23.2,20.2'],
		['M 10.5,18.5 L 10.5,20', 'M 23.5,18.5 L 23.5,20'],
		['M 10.8,18 L 10.8,19.8', 'M 23.8,18 L 23.8,19.8'],
		['M 11.1,17.5 L 11.1,19.6', 'M 24.1,17.5 L 24.1,19.6'],
		['M 11.4,17 L 11.4,19.4', 'M 24.4,17 L 24.4,19.4'],
		['M 11.7,16.5 L 11.7,19.2', 'M 24.7,16.5 L 24.7,19.2'],
		['M 12,16 L 12,18.2', 'M 25,16 L 25,18.2'], // Sad
		['M 12.1,15.85 L 12.1,18.05', 'M 25.2,15.85 L 25.2,18.05'],
		['M 12.2,15.7 L 12.2,17.9', 'M 25.4,15.7 L 25.4,17.9'],
		['M 12.3,15.55 L 12.3,17.75', 'M 25.6,15.55 L 25.6,17.75'],
		['M 12.4,15.4 L 12.4,17.6', 'M 25.8,15.4 L 25.8,17.6'],
		['M 12.5,15.25 L 12.5,17.45', 'M 26,15.25 L 26,17.45'],
		['M 12.6,15.1 L 12.6,17.3', 'M 26.2,15.1 L 26.2,17.3'],
		['M 12.7,14.95 L 12.7,17.15', 'M 26.4,14.95 L 26.4,17.15'],
		['M 12.8,14.8 L 12.8,17', 'M 26.6,14.8 L 26.6,17'],
		['M 12.9,14.65 L 12.9,16.85', 'M 26.8,14.65 L 26.8,16.85'],
		['M 13,14.5 L 13,16.7', 'M 27,14.5 L 27,16.7'], // Neutral dots
		['M 13.3,14.3 L 13.3,16.5', 'M 27.3,14.3 L 27.3,16.5'],
		['M 13.6,14.1 L 13.6,16.3', 'M 27.6,14.1 L 27.6,16.3'],
		['M 13.9,13.9 L 13.9,16.1', 'M 27.9,13.9 L 27.9,16.1'],
		['M 14.2,13.7 L 14.2,15.9', 'M 28.2,13.7 L 28.2,15.9'],
		['M 14.5,13.5 L 14.5,15.7', 'M 28.5,13.5 L 28.5,15.7'],
		['M 14.8,13.3 L 14.8,15.5', 'M 28.8,13.3 L 28.8,15.5'],
		['M 15.1,13.1 L 15.1,15.3', 'M 29.1,13.1 L 29.1,15.3'],
		['M 15.4,12.9 L 15.4,15.1', 'M 29.4,12.9 L 29.4,15.1'],
		['M 15.7,12.7 L 15.7,15.1', 'M 29.7,12.7 L 29.7,15.1'],
		['M 16,12.5 L 16,15', 'M 30,12.5 L 30,15'], // Happy
		['M 16.2,12.4 L 16.2,14.9', 'M 30.15,12.4 L 30.15,14.9'],
		['M 16.4,12.3 L 16.4,14.8', 'M 30.3,12.3 L 30.3,14.8'],
		['M 16.6,12.2 L 16.6,14.7', 'M 30.45,12.2 L 30.45,14.7'],
		['M 16.8,12.1 L 16.8,14.6', 'M 30.6,12.1 L 30.6,14.6'],
		['M 17,12.0 L 17,14.5', 'M 30.75,12.0 L 30.75,14.5'],
		['M 17.2,11.9 L 17.2,14.4', 'M 30.9,11.9 L 30.9,14.4'],
		['M 17.4,11.8 L 17.4,14.3', 'M 31.05,11.8 L 31.05,14.3'],
		['M 17.6,11.7 L 17.6,14.2', 'M 31.2,11.7 L 31.2,14.2'],
		['M 17.8,11.6 L 17.8,14.1', 'M 31.35,11.6 L 31.35,14.1'],
		['M 18,11.5 L 18,14', 'M 31.5,11.5 L 31.5,14'], // Very happy
	],
	mouth: [
		'M 9,31 C 13,26.5 18,26.5 22,31', // very sad
		'M 9.3,30.75 C 13.3,26.4 18.3,26.4 22.3,30.75',
		'M 9.6,30.5 C 13.6,26.3 18.6,26.3 22.6,30.5',
		'M 9.9,30.25 C 13.9,26.2 18.9,26.2 22.9,30.25',
		'M 10.2,30 C 14.2,26.1 19.2,26.1 23.2,30',
		'M 10.5,29.75 C 14.5,26 19.5,26 23.5,29.75',
		'M 10.8,29.5 C 14.8,25.9 19.8,25.9 23.8,29.5',
		'M 11.1,29.25 C 15.1,25.8 20.1,25.8 24.1,29.25',
		'M 11.4,29 C 15.4,25.7 20.4,25.7 24.4,29',
		'M 11.7,28.75 C 15.7,25.6 20.7,25.6 24.7,28.75',
		'M 12,28.5 C 16,25.5 21,25.5 25,28.5', // sad
		'M 12.37,28.25 C 16.27,25.45 21.17,25.45 25.07,28.25',
		'M 12.74,28 C 16.54,25.4 21.34,25.4 25.14,28',
		'M 13.11,27.75 C 16.81,25.35 21.51,25.35 25.21,27.75',
		'M 13.48,27.5 C 17.08,25.3 21.68,25.3 25.28,27.5',
		'M 13.85,27.25 C 17.35,25.25 21.85,25.25 25.35,27.25',
		'M 14.22,27 C 17.62,25.2 22.02,25.2 25.42,27',
		'M 14.59,26.75 C 17.89,25.15 22.19,25.15 25.49,26.75',
		'M 14.96,26.5 C 18.16,25.1 22.36,25.1 25.56,26.5',
		'M 15.33,26.25 C 18.43,25.05 22.53,25.05 25.63,26.25',
		'M 15.7,26 C 15.7,26 25.2,26 25.2,26', // neutral
		'M 15.8,25.75 C 16.3,26.05 25.4,26.05 25.7,25.75',
		'M 15.9,25.5 C 16.9,26.1 25.6,26.1 26.2,25.5',
		'M 16,25.25 C 17.5,26.15 25.8,26.15 26.7,25.25',
		'M 16.1,25 C 18.1,26.2 26,26.2 27.2,25',
		'M 16.2,24.75 C 18.7,26.25 26.2,26.25 27.7,24.75',
		'M 16.3,24.5 C 19.3,26.3 26.4,26.3 28.2,24.5',
		'M 16.4,24.25 C 19.9,26.35 26.6,26.35 28.7,24.25',
		'M 16.6,24 C 20.5,26.4 26.8,26.4 29.2,24',
		'M 16.8,23.75 C 20.75,26.45 27,26.45 29.6,23.75',
		'M 17,23.5 C 21,26.5 26,26.5 30,23.5', // happy
		'M 17.4,23.2 C 21.1,26.6 26.2,26.6 29.9,23.2',
		'M 17.8,22.9 C 21.2,26.7 26.4,26.7 29.8,22.9',
		'M 18.2,22.6 C 21.3,26.8 26.6,26.8 29.7,22.6',
		'M 18.6,22.3 C 21.4,26.9 26.8,26.9 29.6,22.3',
		'M 19.0,22.0 C 21.5,27.0 27.0,27.0 29.5,22.0',
		'M 19.4,21.7 C 21.6,27.1 27.2,27.1 29.4,21.7',
		'M 19.8,21.4 C 21.7,27.2 27.4,27.2 29.3,21.4',
		'M 20.2,21.1 C 21.8,27.3 27.6,27.3 29.2,21.1',
		'M 20.6,20.8 C 21.9,27.4 27.8,27.4 29.1,20.8',
		'M 21,20.5 C 22,27.5 28,27.5 28.5,20.5', // very happy
	],
	openedMouth: [
		// from happy to very happy mouth transition
		['M 24,25.5 L 24,25.5', 'M 24,25.5 L 24,25.5', '0.0'],
		['M 24,25.5 L 24,25.5', 'M 24,25.5 L 24,25.5', '0.25'],
		['M 24,25.5 L 24,25.5', 'M 24,25.5 L 24,25.5', '0.5'],
		['M 24,25.5 L 24,25.5', 'M 24,25.5 L 24,25.5', '0.75'],
		['M 24,25.5 L 24,25.5', 'M 24,25.5 L 24,25.5', '1.0'],
		['M 24,25.5 L 24,25.5', 'M 24,25.5 L 24,25.5', '1.25'],
		['M 23,25.25 L 27,25.25', 'M 23,24.5 L 28,24.5', '1.5'],
		['M 22,25 L 27,25', 'M 21,23.5 L 28,23.5', '1.75'],
		['M 22,24.5 L 27,24.5', 'M 21,23 L 28,23', '2.0'],
		['M 22,24 L 27,24', 'M 21,22 L 28,22', '2.45'],
		['M 22,23 L 27,23', 'M 21,20.5 L 28,20.5', '2.75'],
	],
};

interface EmojiProps extends BoxProps {
	value?: number; // decimal from 1.0 to 5.0, undefined shows neutral, less than 1 shows very sad, more than 5 shows very happy
	showScore?: boolean;
}

export const Emoji = forwardRef<HTMLDivElement, EmojiProps>(({ value = 3, showScore = true, ...props }, ref) => {
	const roundedValue = Math.round(value * 10) / 10;
	const cappedValue = Math.min(Math.max(roundedValue, 1), 5); // cap the value between 1 and 5

	// Map the value to a position in a range of 50
	// 1.0 -> 0, 2.5 -> 25, 5.0 -> 50
	let position;
	if (cappedValue <= 2.5) {
		// 1.0-2.5 maps to 0-25
		position = ((cappedValue - 1) * 25) / 1.5;
	} else {
		// 2.5-5.0 maps to 25-50
		position = 25 + ((cappedValue - 2.5) * 25) / 2.5;
	}

	// Convert position (0-50) to indexValue (0-40)
	const indexValue = Math.round((position * 40) / 50);

	const transition: Transition = { duration: 0.25, ease: 'easeInOut' };
	const expression = useMemo(
		() => ({
			eyes: EXPRESSIONS.eyes[indexValue],
			mouth: EXPRESSIONS.mouth[indexValue],
			openedMouth: EXPRESSIONS.openedMouth[Math.max(0, indexValue - 30)], // opened mouth only from 30 to 40
			bgColor: COLORS[indexValue],
			expressionColor: INSIDE_COLORS,
		}),
		[indexValue]
	);

	return (
		<Box h={10} w={10} position="relative" ref={ref} {...props} rounded="full">
			<motion.svg
				viewBox="0 0 40 40"
				style={{ width: '100%', height: '100%', borderRadius: '50%' }}
				animate={{ backgroundColor: expression.bgColor }}
				transition={transition}
				initial={false}
				pointerEvents="none"
			>
				{/* Left Eye */}
				<motion.path
					d={expression.eyes[0]}
					stroke={expression.expressionColor}
					strokeWidth="3.8"
					strokeLinecap="round"
					initial={false}
					animate={{ d: expression.eyes[0] }}
					transition={transition}
				/>

				{/* Right Eye */}
				<motion.path
					d={expression.eyes[1]}
					stroke={expression.expressionColor}
					strokeWidth="3.8"
					strokeLinecap="round"
					initial={false}
					animate={{ d: expression.eyes[1] }}
					transition={transition}
				/>

				{/* Expression Mouth */}
				<motion.path
					d={expression.mouth}
					stroke={expression.expressionColor}
					strokeWidth="2.75"
					strokeLinecap="round"
					initial={false}
					animate={{ d: expression.mouth }}
					transition={transition}
				/>

				{/* Expression Mouth Very Happy Fill */}
				<motion.path
					d={expression.openedMouth[0]}
					strokeWidth={expression.openedMouth[2]}
					stroke={expression.expressionColor}
					strokeLinecap="round"
					initial={false}
					animate={{ d: expression.openedMouth[0], strokeWidth: expression.openedMouth[2] }}
					transition={transition}
				/>
				<motion.path
					d={expression.openedMouth[1]}
					strokeWidth={expression.openedMouth[2]}
					stroke={expression.expressionColor}
					strokeLinecap="round"
					initial={false}
					animate={{ d: expression.openedMouth[1], strokeWidth: expression.openedMouth[2] }}
					transition={transition}
				/>
			</motion.svg>

			{showScore && (
				<Box
					bgColor="bg.surface"
					color="text.default"
					fontSize="xs"
					fontWeight="semibold"
					px={1}
					py={0}
					pointerEvents="none"
					pos="absolute"
					rounded="sm"
					shadow="sm"
					right="-10px"
					top="-10px"
				>
					{roundedValue.toFixed(1)}
				</Box>
			)}
		</Box>
	);
});

Emoji.displayName = 'Emoji';
